import { useEffect, lazy, Suspense } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Marquee } from "@/components/landing/Marquee";
import { SourcingBand } from "@/components/landing/SourcingBand";
import { STATE_BY_SLUG } from "@/config/states";
import { INTAKE_URLS } from "@/config/intake";
import { ArrowRight, Check, MapPin } from "lucide-react";
import { motion } from "framer-motion";

const Categories = lazy(() => import("@/components/landing/Categories").then(m => ({ default: m.Categories })));
const HowItWorks = lazy(() => import("@/components/landing/HowItWorks").then(m => ({ default: m.HowItWorks })));
const MeetClinicians = lazy(() => import("@/components/landing/MeetClinicians").then(m => ({ default: m.MeetClinicians })));
const Pricing = lazy(() => import("@/components/landing/Pricing").then(m => ({ default: m.Pricing })));
const FAQ = lazy(() => import("@/components/landing/FAQ").then(m => ({ default: m.FAQ })));
const FinalCTA = lazy(() => import("@/components/landing/FinalCTA").then(m => ({ default: m.FinalCTA })));
const Footer = lazy(() => import("@/components/landing/Footer").then(m => ({ default: m.Footer })));
const TrustBar = lazy(() => import("@/components/landing/TrustBar").then(m => ({ default: m.TrustBar })));

const State = () => {
  const { state: slug } = useParams<{ state: string }>();
  const state = slug ? STATE_BY_SLUG[slug] : undefined;

  useEffect(() => {
    if (!state) return;
    document.title = `Olive Health — Telehealth membership in ${state.name}`;
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      `Olive Health is now ${state.eligible ? "available" : "coming soon"} in ${state.name}. GLP-1, peptides, hormones, and longevity protocols from US-licensed clinicians, shipped to your door.`,
    );

    const ldId = `ld-state-${state.code}`;
    document.getElementById(ldId)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = ldId;
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      name: `Olive Health — ${state.name}`,
      areaServed: { "@type": "State", name: state.name },
      medicalSpecialty: ["PrimaryCare", "Endocrine"],
    });
    document.head.appendChild(s);
    return () => { document.getElementById(ldId)?.remove(); };
  }, [state]);

  if (!state) return <Navigate to="/" replace />;

  const intakeHref = `${INTAKE_URLS.default}?state=${state.code}`;

  return (
    <main className="relative min-h-screen bg-background">
      <Header />

      <section className="relative pt-28 md:pt-36 pb-16 md:pb-24 bg-gradient-warm border-b border-border">
        <div className="container-wide max-w-3xl">
          <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-accent font-medium">
            <MapPin className="size-3.5" aria-hidden /> {state.name}
          </p>
          <h1 className="font-display text-[clamp(2.4rem,5.5vw,4.5rem)] leading-[1.02] mt-5 text-foreground text-balance">
            Telehealth membership for {state.name}.
          </h1>
          <p className="mt-6 text-lg text-foreground/70 leading-relaxed max-w-xl">
            One membership covers GLP-1, peptides, hormones, longevity, and labs — prescribed
            by clinicians licensed in {state.name}, shipped to your door.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mt-8 inline-flex flex-wrap items-center gap-3"
          >
            {state.eligible ? (
              <>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 text-accent text-xs px-3 py-1.5 font-medium">
                  <Check className="size-3.5" strokeWidth={2.5} /> Live in {state.name}
                </span>
                <a
                  href={intakeHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium hover:opacity-90 transition"
                >
                  Start membership <ArrowRight className="size-4" />
                </a>
              </>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary text-foreground/70 text-xs px-3 py-1.5 font-medium">
                Coming soon to {state.name}
              </span>
            )}
          </motion.div>
        </div>
      </section>

      <Marquee />
      <Suspense fallback={null}>
        <SourcingBand />
        <Categories />
        <HowItWorks />
        <MeetClinicians />
        <Pricing />
        <FAQ />
        <FinalCTA />
        <TrustBar />
        <Footer />
      </Suspense>
    </main>
  );
};

export default State;
